import { ScorecardViewPermissions } from 'features/TabbedJobView/HiringTeamTab/types';
import { AlertSeverity } from 'sharedComponents/Alert/types';
import { Answer, Question, ReferenceCheck } from 'types';
import { CurrentlyOpenModal } from '../Modal/types';
import { CandidateFeedbackFinalRecommendation, CandidateFeedbackModalTypes } from './enum';
import { LongCandidateFeedbackFinalRecommendation } from './enum';

export const CandidateFeedbackListModalPane = {
  SCORECARD_LIST: 'scorecardList',
  REFERENCE_LIST: 'referenceList',
  SCORECARD_CREATE: 'scorecardCreate',
  REFERENCE_CREATE: 'referenceCreate',
  EDIT: 'edit',
} as const;

export type CandidateFeedbackListModalPaneValue =
  (typeof CandidateFeedbackListModalPane)[keyof typeof CandidateFeedbackListModalPane];

export type CandidateFeedbackModalPane = {
  type: CandidateFeedbackListModalPaneValue;
  scorecardId?: number;
};

export interface CompletionChipProps {
  isComplete: boolean;
  modalType: CandidateFeedbackModalOption;
}

export type CandidateFeedbackModalOption =
  (typeof CandidateFeedbackModalTypes)[keyof typeof CandidateFeedbackModalTypes];

export type ReadableFinalRecommendation =
  | (typeof CandidateFeedbackFinalRecommendation)[keyof typeof CandidateFeedbackFinalRecommendation]
  | (typeof LongCandidateFeedbackFinalRecommendation)[keyof typeof LongCandidateFeedbackFinalRecommendation];

export interface FinalRecommendationDataReturned {
  alertType: AlertSeverity;
  text: string;
}

export type FeedbackAnswer = Pick<
  Answer,
  | 'id'
  | 'is_required'
  | 'answer_text'
  | 'multi_select'
  | 'mc_answer'
  | 'answer_rubric'
  | 'answer_rating'
> & {
  question: CandidateFeedbackQuestion;
  attachment?: string;
};

export interface HiringRequestScorecard {
  answers: FeedbackAnswer[];
  created: string;
  cumulative_score: number | null;
  final_recommendation: ReadableFinalRecommendation | null;
  hiring_request_id: number;
  id: number;
  include_cumulative_score: boolean;
  include_final_recommendation: boolean;
  include_maybe_option: null;
  include_rating_questions: boolean | null;
  include_rubric_questions: boolean | null;
  is_universal: boolean;
  max_cumulative_score: number | null;
  scorecard_complete: boolean;
  show_answer_on_preview: boolean | null;
  show_total_on_preview: boolean | null;
  submitted_by: CandidateFeedbackSubmittedBy;
  template: CandidateFeedbackTemplate;
}

export interface CandidateFeedbackModalProps {
  candidateId: number;
  candidateName: string;
  currentModalOpen: CurrentlyOpenModal;
  hiringRequestId: number;
  onClose: () => void;
  open: boolean;
  onSuccess: (isComplete: boolean) => void;
  roleId: number;
  roleTitle: string;
}

export interface FeedbackStatusProps {
  cumulativeScore: number;
  maxCumulativeScore: number;
  finalRecommendation: ReadableFinalRecommendation;
  showTotalOnPreview: boolean;
  hasRecommendation: boolean;
  hasScore: boolean;
  completionText: string;
}

export interface CandidateFeedbackRowProps {
  data: HiringRequestScorecard | ReferenceCheck;
  onEditScorecardClick: (scorecardId: number) => void;
  modalType: CandidateFeedbackModalOption;
}

export interface CandidateFeedbackSubmittedBy {
  email: string;
  name: string;
}

export interface CandidateFeedbackTemplate {
  id: number;
  title: string;
  visibility?: string;
}

export type CandidateFeedbackQuestion = Pick<
  Answer,
  | 'answer_rating'
  | 'attachment_directions'
  | 'attachment'
  | 'directions'
  | 'id'
  | 'mc_options'
  | 'rubric_columns'
  | 'scale'
  | 'question_type'
  | 'is_required'
> & {
  type: number;
  text: string;
} & Pick<Question, 'mc_options'>;

export type ReferenceFeedbackListPaneProps = Omit<
  ScorecardFeedbackListPaneProps,
  'hiringRequestId'
> & {
  candidateId: number;
};

export interface ScorecardFeedbackListPaneProps {
  hiringRequestId: number;
  onClose: () => void;
  onEditScorecardClick: (scorecardId: number) => void;
  onModalPaneChange: (pane: CandidateFeedbackModalPane) => void;
  roleTitle: string;
  titleText: string;
  modalType: CandidateFeedbackModalOption;
}

export interface RecommendationAlertProps {
  completionText: string;
  cumulativeScore: number;
  finalRecommendation: ReadableFinalRecommendation;
  maxCumulativeScore: number;
  showTotalOnPreview: boolean;
}

export interface RowCTAProps {
  id: number;
  isComplete: boolean;
  collectionId: number;
  onEditScorecardClick: (scorecardId: number) => void;
  modalType: CandidateFeedbackModalOption;
}

export interface ReviewerAndCompletionStatusProps {
  date: string;
  isComplete: boolean;
  submittedByName: string;
}

export interface TemplateAndVisibilityProps {
  templateName: string;
  templateVisibility: ScorecardViewPermissions;
}
